import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardFooter } from '@/components/ui';
import { cn } from '@/lib/utils';
import { formatVolume } from '@/lib/binary-market';
import { CountdownBadge } from './CountdownBadge';

export interface LobbyEventCardProps {
  /** Event id — tapping the card navigates to `/events/:id`. */
  eventId: string;
  /** Event title shown below the eyebrow. */
  name: string;
  /** Accent eyebrow above the title (e.g. "Sports – NBA", "Elections – U.S. Elections"). */
  eyebrow?: string;
  /** Category icon shown in the eyebrow's 30px circle (admin `iconImgUrl`). */
  eyebrowIconUrl?: string | null;
  /** Optional muted subtitle below the title (e.g. the event date, or a live indicator). */
  subtitle?: ReactNode;
  /** Event close time — drives the orange countdown notch inside the last hour. */
  closeTime?: string | null;
  /** Raw volume string for the `$ … Vol` footer. */
  volume: string;
  /** Pre-formatted right-hand footer label, e.g. "3 Markets". */
  marketLabel: string;
  /** The card body between header and footer (a `CardContent` + any headless subscribers). */
  children: ReactNode;
  className?: string;
}

/**
 * Shared lobby-card shell in the V2 design (Figma 5751-141388 "Questions Card"):
 * the tappable card frame, header (30px category icon + purple eyebrow, then the
 * full-width question title), the countdown notch, and the volume/market-count
 * footer. The body — Yes/No rows, option rows, or sports moneyline rows — is
 * passed as children. Composed by both {@link BinaryQuestionCard} and the
 * sports card.
 */
export function LobbyEventCard({
  eventId,
  name,
  eyebrow,
  eyebrowIconUrl,
  subtitle,
  closeTime,
  volume,
  marketLabel,
  children,
  className,
}: LobbyEventCardProps) {
  const navigate = useNavigate();
  return (
    <Card
      className={cn(
        'relative flex h-full cursor-pointer flex-col overflow-hidden rounded-[12px] border-[1.5px] border-stroke-soft',
        className,
      )}
      onClick={() => navigate(`/events/${eventId}`)}
    >
      <CardHeader className="flex-col items-start gap-2 px-5 pt-[18px]">
        {eyebrow && (
          <div className="flex w-full max-w-[263px] items-center gap-1.5">
            {eyebrowIconUrl ? (
              <img src={eyebrowIconUrl} alt="" className="size-[30px] shrink-0 rounded-full object-cover" />
            ) : (
              <div className="size-[30px] shrink-0 rounded-full bg-border" aria-hidden />
            )}
            <span className="min-w-0 truncate text-[16px] font-normal text-purple">{eyebrow}</span>
          </div>
        )}
        <div className="flex min-w-0 flex-col">
          <h3 className="m-0 text-[18px] font-medium leading-normal text-ink">{name}</h3>
          {subtitle && <span className="truncate text-[16px] font-light text-ink">{subtitle}</span>}
        </div>
      </CardHeader>

      <CountdownBadge closeTime={closeTime} />

      {children}

      <CardFooter className="mt-auto border-stroke-soft bg-surface px-5 py-2 pb-3">
        <span className="text-[16px] tracking-[-0.32px] text-ink-muted">$ {formatVolume(volume)} Vol</span>
        <span className="text-[16px] tracking-[-0.32px] text-purple underline">{marketLabel}</span>
      </CardFooter>
    </Card>
  );
}
